import React, { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import dayjs from 'dayjs';
import { Slideout } from '../../../slideout';
import Avatar from '../../../avatar';
import { GET_PLACEHOLDER_VALUE_HISTORY } from '../../../../api/placeholders/queries';
import type { FieldHistoryTarget } from '../FieldHistoryContext';
import styles from '../styles.module.scss';

interface FieldHistoryEntry {
  value: string | null;
  previous: string | null;
  userId: string | null;
  userName: string | null;
  cause: string;
  at: string | null;
  scope: 'document' | 'dealField';
}

interface FieldHistorySlideoutProps {
  target: FieldHistoryTarget;
  onClose: () => void;
}

/** White, like the avatars in the deal activity rows (`ActivitiesCardHelper`).
 *  History entries carry a name, not an email, so there is no hash for `Avatar`
 *  to derive a per-person colour from — passing one explicitly also avoids the
 *  NaN hue it would compute from an empty address. */
const AVATAR_BACKGROUND = '#ffffff';

/**
 * What each change was. `dealField` entries are recorded once on the shared
 * field and shown in every document linked to it, so they say so.
 */
const CAUSE_LABEL: Record<string, string> = {
  typed: 'Edited',
  contact: 'Contact updated',
  linked: 'Linked to deal field',
  unlinked: 'Unlinked',
  deal_field: 'Deal field sync',
  executed: 'Locked at signing',
  initial: 'Initial value',
};

/** A one-line explanation under the value, in plain words. */
function describe(entry: FieldHistoryEntry): string {
  switch (entry.cause) {
    case 'typed':
      return entry.scope === 'dealField' ? 'Typed in deal fields' : 'Typed in this document';
    case 'contact':
      return 'Updated from the linked contact';
    case 'linked':
      return 'Value came from the deal field it was linked to';
    case 'unlinked':
      return 'Unlinked from the deal field — value kept';
    case 'deal_field':
      return 'Synced from the deal field';
    case 'executed':
      return 'Frozen when the document was signed';
    case 'initial':
      return 'First recorded value';
    default:
      return 'Changed';
  }
}

/** Empty values read as a word, matching the sidebar's own "(empty)" convention. */
const display = (value: string | null): string => (value && value.length ? value : '(empty)');

/** "Aug 30, 2026 · 12:04 PM" — the absolute stamp, not a relative one: a change
 *  log is read to find out exactly when something happened. */
const stamp = (at: string | null): string => (at ? dayjs(at).format('MMM D, YYYY · h:mm A') : '');

const FieldHistorySlideout: React.FC<FieldHistorySlideoutProps> = ({ target, onClose }) => {
  const variables = useMemo(
    () =>
      target.scope === 'document'
        ? { placeholderUuid: target.placeholderUuid, linkableId: target.linkableId }
        : { dealFieldGroupId: target.dealFieldGroupId },
    [target]
  );

  // Values change from several places at once (a contact edit reaches this field
  // through its deal field), so a cached timeline goes stale without the user
  // touching this document at all.
  const { data, loading, error } = useQuery(GET_PLACEHOLDER_VALUE_HISTORY, {
    variables,
    fetchPolicy: 'cache-and-network',
  });

  const entries: FieldHistoryEntry[] = data?.placeholderValueHistory ?? [];

  return (
    <Slideout isOpen onClose={onClose} title={`History — ${target.title}`} width="medium">
      <div className={styles.historyContent}>
        {loading && entries.length === 0 && (
          <div className={styles.loading}>
            <div className={styles.spinner} />
          </div>
        )}

        {error && !loading && (
          <div className={styles.emptyState}>
            <p>That history could not be loaded.</p>
          </div>
        )}

        {!loading && !error && entries.length === 0 && (
          <div className={styles.emptyState}>
            <p>No changes recorded yet.</p>
            <p className={styles.fieldsSubtitle}>
              Changes to this field will be listed here, with who made them and when.
            </p>
          </div>
        )}

        {entries.length > 0 && (
          <>
            <p className={styles.historyIntro}>
              {entries.length} {entries.length === 1 ? 'change' : 'changes'}, most recent first
            </p>
            <ol className={styles.historyList}>
              {entries.map((entry, index) => (
                <li className={styles.historyRow} key={`${entry.at}-${index}`}>
                  <div className={styles.historyHeader}>
                    <Avatar
                      email=""
                      fullName={entry.userName || 'System'}
                      size={24}
                      fontSize="10px"
                      backgroundColor={AVATAR_BACKGROUND}
                      className={styles.historyAvatar}
                    />
                    <span className={styles.historyActor}>{entry.userName || 'System'}</span>
                    <span className={styles.historyStamp}>{stamp(entry.at)}</span>
                  </div>

                  <div className={styles.historyChange}>
                    {entry.previous !== null && entry.previous !== entry.value && (
                      <>
                        <span className={styles.historyOldValue} title={display(entry.previous)}>
                          {display(entry.previous)}
                        </span>
                        <span className={styles.historyArrow} aria-hidden="true">
                          →
                        </span>
                      </>
                    )}
                    <span className={styles.historyNewValue} title={display(entry.value)}>
                      {display(entry.value)}
                    </span>
                  </div>

                  <div className={styles.historyFooter}>
                    <span className={styles.historyCausePill}>{CAUSE_LABEL[entry.cause] ?? 'Changed'}</span>
                    {/* A deal-field change is recorded once and reaches every
                        document linked to it — worth saying, or it looks like
                        someone edited this document. */}
                    {entry.scope === 'dealField' && (
                      <span className={styles.historyScopePill} title="Applies to every linked document">
                        Deal field
                      </span>
                    )}
                    <span className={styles.historyCause}>{describe(entry)}</span>
                  </div>
                </li>
              ))}
            </ol>
          </>
        )}
      </div>
    </Slideout>
  );
};

export default FieldHistorySlideout;
